import { MOIS } from './dates'

// Périodes canoniques des échéances : « 2027-05 », « 2026-T3 », « 2026 ».
// Une échéance est unique par (regle_id, periode) : la forme doit donc être stable.

export type Periode =
  | { type: 'mois'; annee: number; mois: number }
  | { type: 'trimestre'; annee: number; trimestre: number }
  | { type: 'annee'; annee: number }

function verifierAnnee(annee: number): void {
  if (!Number.isInteger(annee) || annee < 1000 || annee > 9999) {
    throw new RangeError(`Année invalide : ${annee}`)
  }
}

export function periodeMensuelle(annee: number, mois: number): string {
  verifierAnnee(annee)
  if (!Number.isInteger(mois) || mois < 1 || mois > 12) {
    throw new RangeError(`Mois invalide : ${mois}`)
  }
  return `${annee}-${String(mois).padStart(2, '0')}`
}

export function periodeTrimestrielle(annee: number, trimestre: number): string {
  verifierAnnee(annee)
  if (!Number.isInteger(trimestre) || trimestre < 1 || trimestre > 4) {
    throw new RangeError(`Trimestre invalide : ${trimestre}`)
  }
  return `${annee}-T${trimestre}`
}

export function periodeAnnuelle(annee: number): string {
  verifierAnnee(annee)
  return String(annee)
}

const MOTIF = /^(\d{4})(?:-(?:(0[1-9]|1[0-2])|T([1-4])))?$/

export function analyserPeriode(texte: string): Periode | null {
  const m = MOTIF.exec(texte)
  if (!m) return null
  const annee = Number(m[1])
  if (m[2] !== undefined) return { type: 'mois', annee, mois: Number(m[2]) }
  if (m[3] !== undefined) return { type: 'trimestre', annee, trimestre: Number(m[3]) }
  return { type: 'annee', annee }
}

export function estPeriodeCanonique(texte: string): boolean {
  return analyserPeriode(texte) !== null
}

/** Dernier jour (AAAA-MM-JJ) de la période canonique ; null pour une autre forme. */
export function dernierJourDePeriode(texte: string): string | null {
  const p = analyserPeriode(texte)
  if (!p) return null
  const mois = p.type === 'mois' ? p.mois : p.type === 'trimestre' ? p.trimestre * 3 : 12
  // Jour 0 du mois suivant : le dernier de celui-ci.
  const jour = new Date(Date.UTC(p.annee, mois, 0)).getUTCDate()
  return `${p.annee}-${String(mois).padStart(2, '0')}-${String(jour).padStart(2, '0')}`
}

/** « 2027-05 » devient « mai 2027 », « 2026-T3 » devient « 3e trimestre 2026 ». */
export function formaterPeriode(texte: string): string {
  const p = analyserPeriode(texte)
  if (!p) return texte
  if (p.type === 'mois') return `${MOIS[p.mois - 1]} ${p.annee}`
  if (p.type === 'trimestre')
    return `${p.trimestre === 1 ? '1er' : `${p.trimestre}e`} trimestre ${p.annee}`
  return String(p.annee)
}
